<script lang="ts" setup>
interface Props {
  backgroundImage: string;
  hasBorder?: boolean;
  hasPadding?: boolean;
  canScale?: boolean;
  // The whole card acts as one button by default. Set false when the card only frames its own
  // controls — a real button inside a role="button" is invalid ARIA and reads as one big control.
  interactive?: boolean;
  // Below-the-fold usages should pass 'lazy' so the background doesn't compete with above-fold
  // content; the default keeps the eager behaviour callers had before.
  imgLoading?: 'eager' | 'lazy';
  // Lets slot content poke past the card edges; the background image keeps its own corner
  // rounding so the card silhouette is unchanged.
  allowOverflow?: boolean;
  // Passed to the underlying NuxtImg so ipx generates an appropriately-sized srcset for the
  // card's rendered width (otherwise it reformats to webp but serves the full-resolution
  // source). Uses @nuxt/image responsive shorthand; override per usage for tight byte savings.
  sizes?: string;
}

const props = withDefaults(defineProps<Props>(), {
  hasBorder: true,
  hasPadding: true,
  canScale: true,
  interactive: true,
  imgLoading: 'eager',
  allowOverflow: false,
  sizes: '100vw sm:50vw lg:33vw',
});

const hovered = ref<boolean>(false);

const imageClasses = computed(() => {
  return twMerge(
    cva('transition-all duration-700', {
      variants: {
        hovered: {
          true: 'scale-110',
          false: 'scale-100',
        },
        canScale: {
          true: '',
          false: '',
        },
      },
      compoundVariants: [
        {
          hovered: true,
          canScale: false,
          class: 'scale-100',
        },
      ],
    })({
      hovered: hovered.value,
      canScale: props.canScale,
    }),
  );
});

const cardClasses = computed(() => {
  return twMerge(cva(
    'relative h-full',
    {
      variants: {
        hasBorder: {
          true: 'border-border border',
          false: '',
        },
      },
    },
  )({ hasBorder: props.hasBorder }));
})

const imgClass = computed(() =>
  twMerge(
    'absolute inset-0 z-0 h-full w-full object-cover',
    props.allowOverflow ? 'rounded-3xl' : '',
    imageClasses.value,
  ),
);
</script>

<template>
  <KUICoreCard
    :role="interactive ? 'button' : undefined"
    elevation="none"
    :tabindex="interactive ? 0 : undefined"
    :omit-background="true"
    :padding="hasPadding ? 'sm' : 'none'"
    :overflow="allowOverflow ? 'visible' : 'hidden'"
    rounding="3xl"
    :class="cardClasses"
    @mouseover="hovered = true"
    @mouseout="hovered = false"
  >
    <NuxtPicture
      :src="backgroundImage"
      :sizes="sizes"
      format="avif,webp"
      :img-attrs="{ alt: '', class: imgClass, loading: imgLoading }"
    />
    <div class="relative z-10 h-full">
      <slot name="content" />
    </div>
  </KUICoreCard>
</template>
